import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Building2, 
  FileText, 
  Users, 
  DollarSign, 
  Clock,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  Briefcase,
  Bell,
  MessageSquare,
  ArrowRight
} from "lucide-react";
import { toast } from "sonner";
import ExpiringContractsWidget from "@/components/ExpiringContractsWidget";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function HoldingDashboard() {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    pendingProjects: 0,
    liveProjects: 0,
    activeContracts: 0,
    totalRevenue: 0
  });
  const [pendingProjects, setPendingProjects] = useState([]);
  const [liveProjects, setLiveProjects] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [projectsWithNotifications, setProjectsWithNotifications] = useState([]);

  useEffect(() => {
    fetchDashboardData();
    fetchNotifications();
    fetchProjectsWithNotifications();
  }, [token]);

  const fetchProjectsWithNotifications = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/notifications/projects-with-notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjectsWithNotifications(res.data?.project_ids || []);
    } catch (error) {
      console.error("Error fetching projects with notifications:", error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch pending projects
      const pendingRes = await fetch(`${API_URL}/api/projects-v3/holding/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const pendingData = await pendingRes.json();
      
      // Fetch all projects for live count
      const browseRes = await fetch(`${API_URL}/api/projects-v3/browse`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const browseData = await browseRes.json();
      
      setPendingProjects(Array.isArray(pendingData) ? pendingData.slice(0, 5) : []);
      setLiveProjects(Array.isArray(browseData) ? browseData.slice(0, 5) : []);
      
      setStats({
        pendingProjects: Array.isArray(pendingData) ? pendingData.length : 0,
        liveProjects: Array.isArray(browseData) ? browseData.length : 0,
        activeContracts: 0,
        totalRevenue: 0
      });
      
    } catch (error) {
      console.error("Error fetching dashboard:", error);
      toast.error("Kunne ikke laste dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      setLoadingNotifications(true);
      const res = await axios.get(`${API_URL}/api/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(Array.isArray(res.data) ? res.data.slice(0, 5) : []);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API_URL}/api/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => 
        n.notification_id === notificationId ? { ...n, read: true } : n
      ));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("no-NO", {
      style: "currency",
      currency: "NOK",
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("no-NO", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending_holding_review: { label: "Venter Godkjenning", color: "bg-yellow-500/20 text-yellow-400" },
      approved_pending_client_sign: { label: "Venter Signatur", color: "bg-blue-500/20 text-blue-400" },
      live: { label: "Live", color: "bg-green-500/20 text-green-400" },
      has_applications: { label: "Har Søknader", color: "bg-purple-500/20 text-purple-400" },
      team_selected: { label: "Team Valgt", color: "bg-cyan-500/20 text-cyan-400" },
      in_progress: { label: "Pågår", color: "bg-emerald-500/20 text-emerald-400" }
    };
    
    const config = statusConfig[status] || { label: status, color: "bg-gray-500/20 text-gray-400" };
    return <Badge className={`${config.color} border-0`}>{config.label}</Badge>;
  };

  return (
    <DashboardLayout>
      <div className="space-y-8 p-6" data-testid="holding-dashboard">
        {/* Expiring contracts alert (only shows if there are any) */}
        <ExpiringContractsWidget />

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Holding Dashboard</h1>
            <p className="text-gray-400 mt-1">Velkommen tilbake, {user?.name}</p>
          </div>
          <Link to="/holding/projects">
            <Button className="bg-[#23F7DD] hover:bg-[#1de9cf] text-black">
              <Briefcase className="w-4 h-4 mr-2" />
              Alle Prosjekter
            </Button>
          </Link>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Venter Godkjenning</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats.pendingProjects}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-yellow-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Live Prosjekter</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats.liveProjects}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Aktive Kontrakter</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats.activeContracts}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Total Omsetning</p>
                  <p className="text-3xl font-bold text-white mt-1">{formatCurrency(stats.totalRevenue)}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-[#23F7DD]/20 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-[#23F7DD]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pending Projects */}
          <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
            <CardHeader className="border-b border-[#2a2a4a]">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-400" />
                  Prosjekter til Godkjenning
                </CardTitle>
                <Link to="/holding/projects?status=pending">
                  <Button variant="ghost" size="sm" className="text-[#23F7DD]">
                    Se alle
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 text-center text-gray-400">Laster...</div>
              ) : pendingProjects.length === 0 ? (
                <div className="p-6 text-center text-gray-400">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-400 opacity-50" />
                  <p>Ingen prosjekter venter på godkjenning</p>
                </div>
              ) : (
                <div className="divide-y divide-[#2a2a4a]">
                  {pendingProjects.map((project) => {
                    const hasNotification = projectsWithNotifications.includes(project.project_id);
                    return (
                    <Link 
                      key={project.project_id} 
                      to={`/holding/projects/${project.project_id}`}
                      className={`block p-4 transition-all ${
                        hasNotification 
                          ? "bg-[#23F7DD]/5 border-l-4 border-[#23F7DD] animate-pulse-slow" 
                          : "hover:bg-[#2a2a4a]/50"
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="text-white font-medium truncate">{project.title}</h4>
                            {hasNotification && (
                              <Badge className="bg-red-500/20 text-red-400 border-0 text-xs">
                                <Bell className="w-3 h-3 mr-1" />
                                Ny
                              </Badge>
                            )}
                          </div>
                          <p className="text-gray-400 text-sm mt-1">{project.client_company || project.client_name}</p>
                          <div className="flex items-center gap-3 mt-2 text-sm text-gray-500">
                            <span>{project.location_city}</span>
                            <span>•</span>
                            <span>{project.crew_size} personer</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-[#23F7DD] font-semibold">{formatCurrency(project.proposed_budget)}</p>
                          <p className="text-gray-500 text-xs mt-1">{formatDate(project.created_at)}</p>
                        </div>
                      </div>
                    </Link>
                  )})}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Live Projects */}
          <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
            <CardHeader className="border-b border-[#2a2a4a]">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-green-400" />
                  Live Prosjekter
                </CardTitle>
                <Link to="/holding/projects?status=live">
                  <Button variant="ghost" size="sm" className="text-[#23F7DD]">
                    Se alle
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 text-center text-gray-400">Laster...</div>
              ) : liveProjects.length === 0 ? (
                <div className="p-6 text-center text-gray-400">
                  <Briefcase className="w-12 h-12 mx-auto mb-3 text-gray-600" />
                  <p>Ingen live prosjekter ennå</p>
                </div>
              ) : (
                <div className="divide-y divide-[#2a2a4a]">
                  {liveProjects.map((project) => {
                    const hasNotification = projectsWithNotifications.includes(project.project_id);
                    return (
                    <Link 
                      key={project.project_id} 
                      to={`/holding/projects/${project.project_id}`}
                      className={`block p-4 transition-all ${
                        hasNotification 
                          ? "bg-[#23F7DD]/5 border-l-4 border-[#23F7DD] animate-pulse-slow" 
                          : "hover:bg-[#2a2a4a]/50"
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="text-white font-medium truncate">{project.title}</h4>
                            {hasNotification && (
                              <Badge className="bg-red-500/20 text-red-400 border-0 text-xs">
                                <Bell className="w-3 h-3 mr-1" />
                                Ny
                              </Badge>
                            )}
                          </div>
                          <p className="text-gray-400 text-sm mt-1">{project.client_company || project.client_name}</p>
                          <div className="flex items-center gap-3 mt-2">
                            {getStatusBadge(project.status)}
                            <span className="text-gray-500 text-sm">{project.applications?.length || 0} søknader</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-[#23F7DD] font-semibold">{formatCurrency(project.team_budget || project.final_budget)}</p>
                          {project.days_until_deadline !== undefined && (
                            <p className="text-gray-500 text-xs mt-1">
                              {project.days_until_deadline} dager igjen
                            </p>
                          )}
                        </div>
                      </div>
                    </Link>
                  )})}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Notifications Section */}
        <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
          <CardHeader className="border-b border-[#2a2a4a]">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <Bell className="w-5 h-5 text-[#23F7DD]" />
                Siste Varsler
                {notifications.filter(n => !n.read).length > 0 && (
                  <Badge className="bg-red-500/20 text-red-400 border-0 ml-2">
                    {notifications.filter(n => !n.read).length} nye
                  </Badge>
                )}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loadingNotifications ? (
              <div className="p-6 text-center text-gray-400">Laster varsler...</div>
            ) : notifications.length === 0 ? (
              <div className="p-6 text-center text-gray-400">
                <Bell className="w-12 h-12 mx-auto mb-3 text-gray-600" />
                <p>Ingen varsler ennå</p>
              </div>
            ) : (
              <div className="divide-y divide-[#2a2a4a]">
                {notifications.map((notification) => (
                  <Link
                    key={notification.notification_id}
                    to={notification.link || "#"}
                    onClick={() => !notification.read && markAsRead(notification.notification_id)}
                    className={`block p-4 hover:bg-[#2a2a4a]/50 transition-colors ${
                      !notification.read ? "bg-[#23F7DD]/5 border-l-2 border-[#23F7DD]" : ""
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        notification.type === "new_project" ? "bg-yellow-500/20" :
                        notification.type === "message" ? "bg-blue-500/20" :
                        notification.type === "application" ? "bg-purple-500/20" :
                        "bg-gray-500/20"
                      }`}>
                        {notification.type === "new_project" ? (
                          <Briefcase className="w-5 h-5 text-yellow-400" />
                        ) : notification.type === "message" ? (
                          <MessageSquare className="w-5 h-5 text-blue-400" />
                        ) : notification.type === "application" ? (
                          <Users className="w-5 h-5 text-purple-400" />
                        ) : (
                          <Bell className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`${!notification.read ? "text-white font-medium" : "text-gray-300"} truncate`}>
                          {notification.title}
                        </p>
                        <p className="text-gray-500 text-sm mt-1 line-clamp-2">
                          {notification.message}
                        </p>
                        <p className="text-gray-600 text-xs mt-2">
                          {new Date(notification.created_at).toLocaleString("no-NO", {
                            day: "2-digit",
                            month: "short",
                            hour: "2-digit",
                            minute: "2-digit"
                          })}
                        </p>
                      </div>
                      {notification.link && (
                        <ArrowRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
          <CardHeader>
            <CardTitle className="text-white">Hurtighandlinger</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Link to="/holding/projects">
                <Button variant="outline" className="w-full h-20 flex-col gap-2 border-[#2a2a4a] hover:border-[#23F7DD] hover:bg-[#23F7DD]/10">
                  <Briefcase className="w-6 h-6 text-[#23F7DD]" />
                  <span className="text-white">Prosjekter</span>
                </Button>
              </Link>
              <Link to="/holding/teams">
                <Button variant="outline" className="w-full h-20 flex-col gap-2 border-[#2a2a4a] hover:border-[#23F7DD] hover:bg-[#23F7DD]/10">
                  <Users className="w-6 h-6 text-[#23F7DD]" />
                  <span className="text-white">Teams</span>
                </Button>
              </Link>
              <Link to="/holding/invoices">
                <Button variant="outline" className="w-full h-20 flex-col gap-2 border-[#2a2a4a] hover:border-[#23F7DD] hover:bg-[#23F7DD]/10">
                  <FileText className="w-6 h-6 text-[#23F7DD]" />
                  <span className="text-white">Fakturaer</span>
                </Button>
              </Link>
              <Link to="/holding/settings">
                <Button variant="outline" className="w-full h-20 flex-col gap-2 border-[#2a2a4a] hover:border-[#23F7DD] hover:bg-[#23F7DD]/10">
                  <Building2 className="w-6 h-6 text-[#23F7DD]" />
                  <span className="text-white">Innstillinger</span>
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
